/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useState } from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import { format, isValid, parseISO, subDays } from 'date-fns';
import { Box, Chip, Tooltip, Typography, useMediaQuery } from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers';

import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoRounded from '@mui/icons-material/InfoRounded';

// Utils
import { formatTimeToAmPm } from 'feature/loading-management/utils/timeHelpers';

const BolHistoryDesktop = props => {
  const { bolDate, bolHistory, setBolDate } = props;
  const smUp = useMediaQuery(theme => theme.breakpoints.up('sm'), { noSsr: true });
  const mdUp = useMediaQuery(theme => theme.breakpoints.up('md'), { noSsr: true });
  const customBreak = useMediaQuery(theme => theme.breakpoints.up(1165), { noSsr: true });

  const actualMessage = "The actual time a driver swiped their badge at the terminal's gate.";
  const estimatedMessage =
    "The estimated time that a driver entered the terminal's gate based on the Rack Wait Time model.";
  const waitMessage = (low, high, date, time) =>
    `Based on the Rack Wait Time model, there is a 95% confidence that the wait time was between ${low} and ${high} minutes on ${date} at ${time}`;

  const [showTime, setShowTime] = useState(null);

  const getStyle = timedata => {
    if (timedata <= 30)
      return {
        backgroundColor: 'secondary.light',
        color: 'text.primary',
      };
    if (timedata > 30 && timedata <= 60)
      return {
        backgroundColor: 'secondary.medium',
        color: 'text.primary',
      };
    if (timedata > 60 && timedata <= 90) return { backgroundColor: 'secondary.medium' };
    if (timedata > 90) return { backgroundColor: 'secondary.dark', color: 'white' };
    return { backgroundColor: 'graphic.light', color: 'text.primary' };
  };

  const getTime = str => (str ? format(new Date(str), 'h:mm aa') : 'N/A');

  const handleDateChange = date => {
    if (isValid(date)) {
      setBolDate(format(date, 'yyyy-MM-dd'));
    }
  };

  const handleClick = idx => {
    if (!mdUp) {
      showTime === idx ? setShowTime(null) : setShowTime(idx);
    }
  };

  const renderTime = time =>
    Math.ceil(time) > 90
      ? `>${Math.ceil(time)}${customBreak ? ' mins' : ''}`
      : `${Math.ceil(time)}${customBreak ? ' mins' : ''}`;

  const renderTooltip = message => (
    <Tooltip
      enterTouchDelay={10}
      placement="bottom"
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 197,
            textAlign: 'center',
          },
        },
      }}
      sx={{
        display: { xs: 'none', sm: 'block' },
      }}
      title={message}
    >
      <InfoRounded
        sx={theme => ({
          color: 'graphic.main',
          cursor: 'pointer',
          height: 13,
          mb: { xs: '2px', '@media (min-width: 1165px)': theme.spacing(0.5) },
          ml: '10px',
          width: 13,
        })}
      />
    </Tooltip>
  );

  return (
    <Box
      sx={{
        backgroundColor: 'transparent',
        pb: { xs: 0, md: !bolHistory?.length ? 0 : 4.5 },
      }}
    >
      <Box justifyContent="space-between" my={3} sx={{ display: { xs: 'none', md: 'flex' } }}>
        <Typography variant="h5">BOL History</Typography>
        <DatePicker
          id="bol-date"
          label="Date"
          maxDate={subDays(new Date(), 1)}
          minDate={subDays(new Date(), 90)}
          onChange={handleDateChange}
          value={parseISO(bolDate)}
        />
      </Box>
      {bolHistory?.length ? (
        bolHistory.map(
          (
            {
              id,
              carrierName,
              actualGateInTime,
              estimatedGateInTime,
              actualWaitTime,
              lowWaitTime,
              highWaitTime,
              loadStartTime,
              loadEndTime,
            },
            index
          ) => {
            const gateInTime = actualGateInTime ?? estimatedGateInTime;
            return (
              <Box
                key={id}
                onClick={() => handleClick(index)}
                sx={{
                  borderBottom: theme => `1px solid ${theme.palette.graphic.light}`,
                  marginBottom: { xs: 1, md: 2 },
                  marginTop: { xs: 0, md: 2 },
                  padding: { xs: '16px 16px 18px', md: '0 0 26px' },
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: { xs: 'initial', md: 'space-between' },
                    marginBottom: '10px',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: { xs: 220, md: 390 },
                    }}
                    variant={smUp ? 'h5' : 'body1'}
                  >
                    <b>{getTime(loadEndTime)}</b> {carrierName}
                  </Typography>
                  <Chip
                    label={id}
                    sx={{ bgcolor: 'warning.main', margin: { xs: '0 12px 0 auto', md: 'revert' } }}
                  />
                  {!mdUp && (showTime !== index ? <ExpandMoreIcon /> : <ExpandLessIcon />)}
                </Box>
                {(mdUp || showTime === index) && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '36% 27% 37%',
                        '@media (max-width: 1165px)': '38% 29% 33%',
                      },
                      gridTemplateRows: { xs: 'repeat(3, 1fr)', sm: 'auto' },
                    }}
                  >
                    <Box
                      sx={{
                        '&:last-child': {
                          paddingLeft: { xs: 0, sm: 0.5 },
                        },
                        '&:nth-child(2)': {
                          borderLeft: theme => ({
                            xs: 'none',
                            sm: `1px solid ${theme.palette.graphic.light}`,
                          }),
                          borderRight: theme => ({
                            xs: 'none',
                            sm: `1px solid ${theme.palette.graphic.light}`,
                          }),
                        },
                        display: { xs: 'flex', sm: 'revert' },
                        justifyContent: { xs: 'space-between', sm: 'revert' },
                      }}
                    >
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          mb: 0.5,
                        }}
                      >
                        <Typography variant={customBreak ? 'subtitle1' : 'caption'}>
                          Driver
                        </Typography>
                        <Typography
                          sx={{
                            color: actualGateInTime ? 'inherit' : 'success.main',
                          }}
                          variant={customBreak ? 'subtitle1' : 'caption'}
                        >
                          &nbsp;{actualGateInTime ? 'Actual' : smUp ? 'Estimated' : 'Est.'}
                        </Typography>
                        {renderTooltip(actualGateInTime ? actualMessage : estimatedMessage)}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flex: { xs: '0 0 calc(100% - 116px)', sm: 'revert' },
                          justifyContent: { xs: 'flex-end', sm: 'center' },
                        }}
                      >
                        <Box
                          sx={{
                            '&:nth-child(2)': {
                              borderLeft: theme => ({
                                xs: `1px solid ${theme.palette.graphic.light}`,
                                sm: 'revert',
                              }),
                              pl: 1,
                            },
                            alignItems: 'center',
                            display: { xs: 'flex', sm: 'revert' },
                            mr: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              marginRight: { xs: 0.5, md: 'revert' },
                              textAlign: 'center',
                            }}
                            variant="subtitle2"
                          >
                            Gate In
                          </Typography>
                          <Typography variant={customBreak ? 'h6' : 'subtitle2'}>
                            {getTime(gateInTime)}
                          </Typography>
                        </Box>
                        {actualGateInTime && (
                          <Box
                            sx={{
                              '&:nth-child(2)': {
                                borderLeft: theme => ({
                                  xs: `1px solid ${theme.palette.graphic.light}`,
                                  sm: 'revert',
                                }),
                                pl: 1,
                              },
                              alignItems: 'center',
                              display: 'flex',
                              flexDirection: { xs: 'row', sm: 'column' },
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                marginRight: { xs: 0.5, md: 'revert' },
                                textAlign: 'center',
                              }}
                              variant="subtitle2"
                            >
                              Wait Time
                            </Typography>
                            <Box
                              sx={theme => [
                                {
                                  ...theme.typography.caption,
                                  borderRadius: '4px',
                                  padding: { xs: 0, '@media (min-width: 1165px)': '7px 0' },
                                  textAlign: 'center',
                                  whiteSpace: 'nowrap',
                                  width: { xs: 24, '@media (min-width: 1165px)': 66 },
                                  alignItems: {
                                    xs: 'center',
                                    '@media (min-width: 1165px)': 'initial',
                                  },
                                  display: { xs: 'flex', '@media (min-width: 1165px)': 'block' },
                                  height: { xs: 20, '@media (min-width: 1165px)': 'auto' },
                                  justifyContent: {
                                    xs: 'center',
                                    '@media (min-width: 1165px)': 'initial',
                                  },
                                },
                                getStyle(actualWaitTime),
                              ]}
                            >
                              {renderTime(actualWaitTime)}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        '&:last-child': {
                          paddingLeft: { xs: 0, sm: 0.5 },
                        },
                        '&:nth-child(2)': {
                          borderLeft: theme => ({
                            xs: 'none',
                            sm: `1px solid ${theme.palette.graphic.light}`,
                          }),
                          borderRight: theme => ({
                            xs: 'none',
                            sm: `1px solid ${theme.palette.graphic.light}`,
                          }),
                        },
                        display: { xs: 'flex', sm: 'revert' },
                        justifyContent: { xs: 'space-between', sm: 'revert' },
                      }}
                    >
                      <Typography
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          mb: 0.5,
                        }}
                        variant={customBreak ? 'subtitle1' : 'caption'}
                      >
                        Est. Wait Time
                        {renderTooltip(
                          waitMessage(
                            lowWaitTime,
                            highWaitTime,
                            format(new Date(bolDate), 'MM/dd/yyyy'),
                            formatTimeToAmPm(new Date(gateInTime))
                          )
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flex: { xs: '0 0 calc(100% - 116px)', sm: 'revert' },
                          justifyContent: { xs: 'flex-end', sm: 'center' },
                        }}
                      >
                        <Box
                          sx={{
                            '&:nth-child(2)': {
                              borderLeft: theme => ({
                                xs: `1px solid ${theme.palette.graphic.light}`,
                                sm: 'revert',
                              }),
                              pl: 1,
                            },
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: { xs: 'row', sm: 'column' },
                            justifyContent: 'center',
                            mr: { xs: 1, sm: 3 },
                          }}
                        >
                          <Typography
                            sx={{
                              marginRight: { xs: 0.5, md: 'revert' },
                              textAlign: 'center',
                            }}
                            variant="subtitle2"
                          >
                            Low
                          </Typography>
                          <Box
                            sx={theme => [
                              {
                                ...theme.typography.caption,
                                borderRadius: '4px',
                                padding: { xs: 0, '@media (min-width: 1165px)': '7px 0' },
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                width: { xs: 24, '@media (min-width: 1165px)': 66 },
                                alignItems: {
                                  xs: 'center',
                                  '@media (min-width: 1165px)': 'initial',
                                },
                                display: { xs: 'flex', '@media (min-width: 1165px)': 'block' },
                                height: { xs: 20, '@media (min-width: 1165px)': 'auto' },
                                justifyContent: {
                                  xs: 'center',
                                  '@media (min-width: 1165px)': 'initial',
                                },
                              },
                              getStyle(lowWaitTime),
                            ]}
                          >
                            {renderTime(lowWaitTime)}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            '&:nth-child(2)': {
                              borderLeft: theme => ({
                                xs: `1px solid ${theme.palette.graphic.light}`,
                                sm: 'revert',
                              }),
                              pl: 1,
                            },
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: { xs: 'row', sm: 'column' },
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              marginRight: { xs: 0.5, md: 'revert' },
                              textAlign: 'center',
                            }}
                            variant="subtitle2"
                          >
                            High
                          </Typography>
                          <Box
                            sx={theme => [
                              {
                                ...theme.typography.caption,
                                borderRadius: '4px',
                                padding: { xs: 0, '@media (min-width: 1165px)': '7px 0' },
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                width: { xs: 24, '@media (min-width: 1165px)': 66 },
                                alignItems: {
                                  xs: 'center',
                                  '@media (min-width: 1165px)': 'initial',
                                },
                                display: { xs: 'flex', '@media (min-width: 1165px)': 'block' },
                                height: { xs: 20, '@media (min-width: 1165px)': 'auto' },
                                justifyContent: {
                                  xs: 'center',
                                  '@media (min-width: 1165px)': 'initial',
                                },
                              },
                              getStyle(highWaitTime),
                            ]}
                          >
                            {renderTime(highWaitTime)}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        '&:last-child': {
                          paddingLeft: { xs: 0, sm: 0.5 },
                        },
                        '&:nth-child(2)': {
                          borderLeft: theme => ({
                            xs: 'none',
                            sm: `1px solid ${theme.palette.graphic.light}`,
                          }),
                          borderRight: theme => ({
                            xs: 'none',
                            sm: `1px solid ${theme.palette.graphic.light}`,
                          }),
                        },
                        display: { xs: 'flex', sm: 'revert' },
                        justifyContent: { xs: 'space-between', sm: 'revert' },
                      }}
                    >
                      <Typography
                        sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}
                        variant={customBreak ? 'subtitle1' : 'caption'}
                      >
                        Load Time
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flex: { xs: '0 0 calc(100% - 116px)', sm: 'revert' },
                          justifyContent: { xs: 'flex-end', sm: 'center' },
                        }}
                      >
                        <Box
                          sx={{
                            '&:nth-child(2)': {
                              borderLeft: theme => ({
                                xs: `1px solid ${theme.palette.graphic.light}`,
                                sm: 'revert',
                              }),
                              pl: 1,
                            },
                            alignItems: 'center',
                            display: { xs: 'flex', sm: 'block' },
                            mr: { xs: 1, sm: 3 },
                          }}
                        >
                          <Typography
                            sx={{
                              marginRight: { xs: 0.5, md: 'revert' },
                              textAlign: 'center',
                            }}
                            variant="subtitle2"
                          >
                            Start
                          </Typography>
                          <Typography
                            sx={{ whiteSpace: 'nowrap' }}
                            variant={customBreak ? 'h6' : 'subtitle2'}
                          >
                            {getTime(loadStartTime)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            '&:nth-child(2)': {
                              borderLeft: theme => ({
                                xs: `1px solid ${theme.palette.graphic.light}`,
                                sm: 'revert',
                              }),
                              pl: 1,
                            },
                            alignItems: 'center',
                            display: { xs: 'flex', sm: 'block' },
                          }}
                        >
                          <Typography
                            sx={{
                              marginRight: { xs: 0.5, md: 'revert' },
                              textAlign: 'center',
                            }}
                            variant="subtitle2"
                          >
                            End
                          </Typography>
                          <Typography
                            sx={{ whiteSpace: 'nowrap' }}
                            variant={customBreak ? 'h6' : 'subtitle2'}
                          >
                            {getTime(loadEndTime)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          }
        )
      ) : (
        <Box
          sx={{
            backgroundColor: 'graphic.lighter',
            borderRadius: { xs: 0, md: 0.5 },
            color: 'text.secondary',
            display: 'flex',
            justifyContent: 'center',
            ml: { xs: 0, md: 1 },
            p: 3,
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Box width={280}>
            <BlockRoundedIcon
              sx={{
                fontSize: 32,
              }}
            />
            <Box component="p" fontSize={20} my={1}>
              No available loading history
            </Box>
            <Box component="p" fontSize={16} mb={0.5} mt={0}>
              Try adjusting your search or filter options to see more results
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

BolHistoryDesktop.propTypes = {
  bolDate: string.isRequired,
  bolHistory: arrayOf(
    shape({
      cardInTime: string,
      carrierName: string,
      gateInTime: string,
      id: number,
    })
  ),
  setBolDate: func.isRequired,
};

BolHistoryDesktop.defaultProps = {
  bolHistory: [],
};

export default BolHistoryDesktop;
